import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import { type CustomHostnamePointer, validateCustomHostname } from '@gitbook/app-database';
import { spacing, TextInput, type TextInputFeedback } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, Button, LoadingIndicator } from '@gitbook/spine-react';
import { HTTPError, HTTPStatusCode, Logger, reportException } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient, useSiteContext } from '../../state';
import { capitalizeFirstLetter, createDocumentationURL } from '../../utils';
import { DocumentationLink } from '../Link';

const logger = Logger('app:custom-domain-setup');

interface SelectSubdomainStepProps {
    /** Initial value if custom hostname was already connected. */
    initialValue?: string;

    /** Callback when custom hostname is selected. */
    onSelect: (hostname: string) => void;

    /** Pointer to which to configure the domain. */
    pointer: CustomHostnamePointer;
}

/**
 * Step 1: Enter preferred subdomain.
 */
export function SelectSubdomainStep(
    props: SelectSubdomainStepProps & { title?: string; site?: api.Site }
): React.ReactElement {
    const { pointer, initialValue, onSelect, site, title } = props;
    const [rawHostname, setHostname] = React.useState(initialValue || '');
    const [loading, wrapLoading] = useLoadingState();
    const [setupHostnameError, setSetupHostnameError] = React.useState<string | null>(null);

    const hostname = React.useMemo(() => {
        // allow copy/pasting URLs that include http(s)://
        if (rawHostname.match(/^http(s:|:)\/\/./)) {
            return rawHostname.replace(/^http(s:|:)\/\//, '').replace(/\/$/, '');
        }

        return rawHostname.replace(/\/$/, '');
    }, [rawHostname]);

    const isDomainValid = validateCustomHostname(hostname);
    const setupFailed = setupHostnameError !== null;

    const onConfirm = async () => {
        if (!setupFailed && isDomainValid) {
            await wrapLoading(async () => {
                if (site && site.hostname !== hostname) {
                    try {
                        await apiClient.customHostnames.testCustomHostname(hostname, {
                            target: pointer,
                        });
                    } catch (error: unknown) {
                        if (
                            error instanceof HTTPError &&
                            error.statusCode === HTTPStatusCode.Conflict
                        ) {
                            setSetupHostnameError(error.message);
                        } else if (error instanceof Error) {
                            reportException(error);
                            setSetupHostnameError(
                                'An unexpected error occurred, please try again or contact our support.'
                            );
                            logger.error('Unexpected error: ', error);
                        }
                        return;
                    }
                }
                onSelect(hostname);
            });
        }
    };

    const onChangeText = (newDomain: string) => {
        setSetupHostnameError(null);
        setHostname(newDomain);
    };

    const feedback: TextInputFeedback | undefined =
        !isDomainValid && hostname.length > 0
            ? {
                  type: 'danger',
                  message: 'This hostname is invalid. Only subdomains are allowed.',
              }
            : setupFailed
              ? {
                    type: 'danger',
                }
              : undefined;

    return (
        <div className="flex flex-col">
            <div className="mt-4 flex flex-col items-center px-1 py-10">
                <p className="text-center text-ui-heading-large">
                    {title ??
                        `What domain would you like to connect to the${' '}
                    ${capitalizeFirstLetter(pointer.type)}?`}
                </p>
            </div>
            <div className="flex w-full max-w-[28.5rem] flex-col self-center">
                <TextInput
                    placeholder="Enter preferred subdomain URL e.g docs.myproduct.com"
                    value={hostname}
                    onChangeText={onChangeText}
                    feedback={feedback}
                    style={[spacing.Margin.horizontal400]}
                />
                {setupHostnameError ? (
                    <Alert
                        kind="danger"
                        details={setupHostnameError}
                        action={{
                            label: 'More info',
                            href: createDocumentationURL(
                                'publishing/custom-domain/troubleshooting#domain-already-connected-error-your-subdomain-is-already-configured-for-different-content.'
                            ),
                            target: '_blank',
                        }}
                        className="mt-6 self-center"
                    />
                ) : null}
            </div>
            <div className="mt-6 mb-10 flex items-center justify-center">
                <Icon.InfoCircle size={16} className="mr-2 text-primary" />
                <p className="text-muted text-ui-small">
                    You must own the domain and have the ability to use a subdomain
                </p>
            </div>
            <Button
                label="Next"
                kind="primary"
                disabled={setupFailed || !isDomainValid || loading}
                onPress={onConfirm}
                leadingIcon={loading ? LoadingIndicator : undefined}
                className="mt-6 mb-4 w-full max-w-[16rem] self-center"
            />
            <div className="my-1 flex items-center gap-2 self-center">
                <Icon.HelpCircle size={16} className="text-primary" />
                <DocumentationLink path="publishing/custom-domain">
                    Read our setup guide
                </DocumentationLink>
            </div>
        </div>
    );
}

export function SiteSelectSubdomainStep(
    props: SelectSubdomainStepProps & {
        /** Context for analytics events */
        analyticsContext: analytics.AnalyticsEventContext;
    }
): React.ReactElement {
    const { site } = useSiteContext();

    useSetAnalyticsScreen(
        {
            ...props.analyticsContext,
            screen: 'site_custom_domain',
            screenTab: 'select',
        },
        [props.analyticsContext]
    );

    return (
        <SelectSubdomainStep
            {...props}
            site={site}
            title="What domain would you like to connect to your docs site?"
        />
    );
}
